import "highlight.js/styles/github-dark.css";
import { useEffect, useState } from "react";
import { MAX_PREVIEW_BYTES, codeLanguage, isImageFile } from "../lib/file-preview";
import { getFileViewURL } from "../lib/storage";

export function FilePreview({ storagePath, fileName }: { storagePath: string; fileName: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "too-large" | "error">("loading");
  const image = isImageFile(fileName);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const viewUrl = await getFileViewURL(storagePath);
        if (!alive) return;
        if (image) {
          setUrl(viewUrl);
          setStatus("ready");
          return;
        }
        const res = await fetch(viewUrl);
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        if (!alive) return;
        if (text.length > MAX_PREVIEW_BYTES) {
          setStatus("too-large");
          return;
        }
        const hljs = (await import("highlight.js/lib/common")).default;
        const lang = codeLanguage(fileName);
        const out =
          lang && hljs.getLanguage(lang)
            ? hljs.highlight(text, { language: lang }).value
            : hljs.highlightAuto(text).value;
        if (!alive) return;
        setHtml(out);
        setStatus("ready");
      } catch {
        if (alive) setStatus("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [storagePath, fileName, image]);

  if (status === "loading") {
    return <p className="mt-2 font-mono text-xs text-cyan-300/70">미리보기 불러오는 중…</p>;
  }
  if (status === "too-large") {
    return (
      <p className="mt-2 text-xs text-slate-400">미리보기가 너무 큽니다. 받기로 확인해주세요.</p>
    );
  }
  if (status === "error") {
    return <p className="mt-2 text-xs text-fuchsia-400">미리보기를 불러오지 못했습니다.</p>;
  }
  if (image && url) {
    return (
      <img
        src={url}
        alt={fileName}
        className="mt-2 max-h-96 w-auto rounded-lg border border-slate-800"
      />
    );
  }
  return (
    <pre className="mt-2 max-h-96 overflow-auto rounded-lg border border-slate-800 text-xs">
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: highlight.js output of fetched file text */}
      <code className="hljs block p-3" dangerouslySetInnerHTML={{ __html: html ?? "" }} />
    </pre>
  );
}
